import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, API } from '@/App';
import axios from 'axios';
import MerchantLayout from '@/components/layouts/MerchantLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  User, Mail, Building, CreditCard, Shield, Clock,
  Percent, Info, AlertTriangle, Check, Loader2,
  ShieldCheck, ArrowUp, ArrowDown, Equal, DollarSign, Globe, QrCode, X, Eye, EyeOff
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

const InfoCard = ({ icon: Icon, label, children }) => (
  <div className="flex items-center gap-3.5 p-4 rounded-xl border border-border bg-muted/30" data-testid={`info-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="w-9 h-9 rounded-lg bg-primary/8 flex items-center justify-center shrink-0">
      <Icon className="h-4 w-4 text-primary" />
    </div>
    <div className="min-w-0">
      <p className="text-[11px] text-muted-foreground uppercase tracking-wider font-medium">{label}</p>
      <div className="mt-0.5">{children}</div>
    </div>
  </div>
);

const Tooltip = ({ text }) => {
  const [show, setShow] = useState(false);
  return (
    <span className="relative inline-flex" onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      <Info className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground cursor-help transition-colors" data-testid="tolerance-info-icon" />
      {show && (
        <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-3 py-2 bg-foreground text-background text-xs rounded-lg shadow-lg whitespace-nowrap z-50 max-w-xs text-center" data-testid="tolerance-tooltip">
          If an invoice is for $100 and tolerance is 1%, payments &ge; $99 will be accepted.
        </span>
      )}
    </span>
  );
};

const TABS = [
  { id: 'profile', label: 'Profile Information', icon: User },
  { id: 'security', label: 'Security', icon: Shield },
  { id: 'currency', label: 'Currency', icon: DollarSign },
  { id: 'tolerance', label: 'Payment Tolerance', icon: Percent },
  { id: 'verification', label: 'Payment Verification', icon: ShieldCheck },
];

const MerchantSettings = () => {
  const { user } = useAuth();
  const initials = user?.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'U';
  const [activeTab, setActiveTab] = useState('profile');

  // 2FA state
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [twoFALoading, setTwoFALoading] = useState(true);
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [twoFABtnLoading, setTwoFABtnLoading] = useState(false);

  // Change password state
  const [showPwForm, setShowPwForm] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNextPw, setShowNextPw] = useState(false);

  const handleChangePassword = async () => {
    if (!pwForm.current || !pwForm.next || !pwForm.confirm) return toast.error('All fields are required.');
    if (pwForm.next.length < 8) return toast.error('New password must be at least 8 characters.');
    if (pwForm.next !== pwForm.confirm) return toast.error('New passwords do not match.');
    setPwLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: pwForm.current, new_password: pwForm.next });
      toast.success('Password changed successfully!');
      setShowPwForm(false);
      setPwForm({ current: '', next: '', confirm: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change password.');
    } finally {
      setPwLoading(false);
    }
  };

  const [tolerance, setTolerance] = useState({ enabled: false, percentage: 1.0 });
  const [toleranceLoading, setToleranceLoading] = useState(true);
  const [toleranceSaving, setToleranceSaving] = useState(false);
  const [toleranceDirty, setToleranceDirty] = useState(false);

  const [verification, setVerification] = useState({ method: 'exact_amount' });
  const [verificationLoading, setVerificationLoading] = useState(true);
  const [verificationSaving, setVerificationSaving] = useState(false);
  const [verificationDirty, setVerificationDirty] = useState(false);

  const [preferredCurrency, setPreferredCurrency] = useState('USD');
  const [currencyLoading, setCurrencyLoading] = useState(true);
  const [currencySaving, setCurrencySaving] = useState(false);

  const fetch2FAStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/2fa/status`);
      setTwoFAEnabled(data.totp_enabled);
    } catch { /* ignore */ }
    finally { setTwoFALoading(false); }
  }, []);

  const handle2FASetup = async () => {
    setTwoFABtnLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/2fa/setup`);
      setQrCode(data.qr_code);
      setSecretKey(data.secret);
      setVerifyCode('');
      setShowSetupDialog(true);
    } catch { toast.error('Failed to initiate 2FA setup'); }
    finally { setTwoFABtnLoading(false); }
  };

  const handle2FAEnable = async () => {
    if (verifyCode.length !== 6) return toast.error('Enter the 6-digit code');
    setTwoFABtnLoading(true);
    try {
      await axios.post(`${API}/auth/2fa/enable`, { totp_code: verifyCode });
      setTwoFAEnabled(true);
      setShowSetupDialog(false);
      setVerifyCode('');
      toast.success('Two-factor authentication enabled!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Invalid code'); }
    finally { setTwoFABtnLoading(false); }
  };

  const handle2FADisable = async () => {
    if (verifyCode.length !== 6) return toast.error('Enter the 6-digit code');
    setTwoFABtnLoading(true);
    try {
      await axios.post(`${API}/auth/2fa/disable`, { totp_code: verifyCode });
      setTwoFAEnabled(false);
      setShowDisableDialog(false);
      setVerifyCode('');
      toast.success('Two-factor authentication disabled.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Invalid code'); }
    finally { setTwoFABtnLoading(false); }
  };

  const fetchTolerance = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/settings/payment-tolerance`);
      setTolerance({ enabled: data.enabled, percentage: data.percentage });
    } catch { /* defaults */ }
    finally { setToleranceLoading(false); }
  }, []);

  const fetchVerification = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/settings/payment-verification`);
      setVerification({ method: data.method });
    } catch { /* defaults */ }
    finally { setVerificationLoading(false); }
  }, []);

  const fetchCurrency = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/settings/currency`);
      setPreferredCurrency(data.preferred_currency || 'USD');
    } catch { /* default USD */ }
    finally { setCurrencyLoading(false); }
  }, []);

  useEffect(() => { fetchTolerance(); fetchVerification(); fetchCurrency(); fetch2FAStatus(); }, [fetchTolerance, fetchVerification, fetchCurrency, fetch2FAStatus]);

  const saveTolerance = async () => {
    setToleranceSaving(true);
    try {
      await axios.put(`${API}/settings/payment-tolerance`, tolerance);
      toast.success('Payment tolerance updated');
      setToleranceDirty(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update tolerance');
    } finally { setToleranceSaving(false); }
  };

  const updateTolerance = (field, value) => {
    setTolerance(prev => ({ ...prev, [field]: value }));
    setToleranceDirty(true);
  };

  const saveVerification = async () => {
    setVerificationSaving(true);
    try {
      await axios.put(`${API}/settings/payment-verification`, verification);
      toast.success('Payment verification method updated');
      setVerificationDirty(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally { setVerificationSaving(false); }
  };

  const saveCurrency = async () => {
    setCurrencySaving(true);
    try {
      await axios.put(`${API}/settings/currency`, { preferred_currency: preferredCurrency });
      toast.success('Preferred currency updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update currency');
    } finally { setCurrencySaving(false); }
  };

  const METHODS = {
    exact_amount: {
      label: 'Exact Amount (Recommended)', icon: Equal, color: 'text-blue-500', bg: 'bg-blue-500/10',
      desc: 'Customer pays the exact invoice amount. No adjustment applied.',
      example: 'Invoice: $50.00 USDT → Customer pays: $50.000 USDT',
      pros: 'Simplest method. Works well when only one invoice is active per wallet.',
      cons: 'System will require TXID verification for payment verification.'
    },
    round_up: {
      label: 'Round Up', icon: ArrowUp, color: 'text-emerald-500', bg: 'bg-emerald-500/10',
      desc: 'Adds a tiny amount (+$0.001 increments) to make each payable amount unique on-chain.',
      example: 'Invoice: $10.00 → Pays: $10.001 | Next: $10.002',
      pros: 'Guarantees unique amounts. Merchant receives slightly more. Up to 300 unique invoices per amount.',
      cons: 'Customer sees a slightly different amount than the original.'
    },
    round_down: {
      label: 'Round Down', icon: ArrowDown, color: 'text-amber-500', bg: 'bg-amber-500/10',
      desc: 'Subtracts a tiny amount (-$0.001 increments) to create unique amounts on-chain.',
      example: 'Invoice: $10.00 → Pays: $9.999 | Next: $9.998',
      pros: 'Unique amounts. Customer pays slightly less. Up to 300 unique invoices per amount.',
      cons: 'Merchant receives slightly less. Must stay within tolerance to avoid rejection.'
    }
  };

  return (
    <MerchantLayout title="Settings">
      {/* 2FA Setup Dialog */}
      <Dialog open={showSetupDialog} onOpenChange={(o) => { setShowSetupDialog(o); if (!o) setVerifyCode(''); }}>
        <DialogContent className="max-w-sm" data-testid="2fa-setup-dialog">
          <DialogHeader>
            <DialogTitle>Set Up Two-Factor Authentication</DialogTitle>
            <DialogDescription>Scan the QR code with Google Authenticator or Authy, then enter the 6-digit code to confirm.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {qrCode && (
              <div className="flex justify-center">
                <img src={`data:image/png;base64,${qrCode}`} alt="2FA QR Code" className="w-48 h-48 rounded-lg border border-border" data-testid="2fa-qr-code" />
              </div>
            )}
            <div className="bg-muted/40 rounded-lg p-3 text-center">
              <p className="text-xs text-muted-foreground mb-1">Can't scan? Enter this key manually:</p>
              <p className="text-xs font-mono font-bold text-foreground tracking-widest break-all">{secretKey}</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Verification Code</Label>
              <Input
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="text-center text-lg font-mono tracking-widest"
                data-testid="2fa-verify-input"
              />
            </div>
            <Button className="w-full" onClick={handle2FAEnable} disabled={twoFABtnLoading || verifyCode.length !== 6} data-testid="2fa-enable-btn">
              {twoFABtnLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
              Enable 2FA
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* 2FA Disable Dialog */}
      <Dialog open={showDisableDialog} onOpenChange={(o) => { setShowDisableDialog(o); if (!o) setVerifyCode(''); }}>
        <DialogContent className="max-w-sm" data-testid="2fa-disable-dialog">
          <DialogHeader>
            <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
            <DialogDescription>Enter your current 6-digit authenticator code to disable 2FA.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Authenticator Code</Label>
              <Input
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="text-center text-lg font-mono tracking-widest"
                data-testid="2fa-disable-verify-input"
              />
            </div>
            <Button variant="destructive" className="w-full" onClick={handle2FADisable} disabled={twoFABtnLoading || verifyCode.length !== 6} data-testid="2fa-disable-confirm-btn">
              {twoFABtnLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <X className="h-4 w-4 mr-2" />}
              Disable 2FA
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="space-y-6 max-w-4xl" data-testid="settings-page">

        {/* Tab Navigation */}
        <div className="flex gap-1 bg-[hsl(var(--card))] border border-border rounded-lg p-1 overflow-x-auto" data-testid="settings-tabs">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                data-testid={`settings-tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Profile Information Tab */}
        {activeTab === 'profile' && (
          <Card className="border-border bg-card" data-testid="profile-tab-content">
            <CardHeader className="pb-4">
              <CardTitle className="text-base font-semibold">Profile Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary/20 to-purple-400/20 border border-primary/10 flex items-center justify-center">
                  <span className="text-xl font-bold text-primary">{initials.toLowerCase()}</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold tracking-tight">{user?.full_name}</h3>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <InfoCard icon={User} label="Full Name">
                  <p className="text-sm font-medium text-foreground">{user?.full_name}</p>
                </InfoCard>
                <InfoCard icon={Mail} label="Email">
                  <p className="text-sm font-medium text-foreground">{user?.email}</p>
                </InfoCard>
                <InfoCard icon={Building} label="Business Name">
                  <p className="text-sm font-medium text-foreground">{user?.business_name || 'Not set'}</p>
                </InfoCard>
                <InfoCard icon={CreditCard} label="Current Plan">
                  <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 font-semibold text-[11px]">
                    {user?.plan?.toUpperCase() || 'STARTER'}
                  </Badge>
                </InfoCard>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <Card className="border-border bg-card" data-testid="security-tab-content">
            <CardHeader className="pb-4">
              <CardTitle className="text-base font-semibold">Security</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="divide-y divide-border">
                <div className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-primary/8 flex items-center justify-center shrink-0">
                        <Shield className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Password</p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">Update your account password</p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" className="text-xs" onClick={() => { setShowPwForm(v => !v); setPwForm({ current: '', next: '', confirm: '' }); }} data-testid="change-password-btn">
                      {showPwForm ? 'Cancel' : 'Change'}
                    </Button>
                  </div>
                  {showPwForm && (
                    <div className="mt-4 space-y-3 pl-12" data-testid="change-password-form">
                      <div className="relative">
                        <Input type={showCurrentPw ? 'text' : 'password'} placeholder="Current password" value={pwForm.current} onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))} className="pr-9 text-sm" data-testid="current-password-input" />
                        <button type="button" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setShowCurrentPw(v => !v)}>
                          {showCurrentPw ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                        </button>
                      </div>
                      <div className="relative">
                        <Input type={showNextPw ? 'text' : 'password'} placeholder="New password (min 8 chars)" value={pwForm.next} onChange={e => setPwForm(f => ({ ...f, next: e.target.value }))} className="pr-9 text-sm" data-testid="new-password-input" />
                        <button type="button" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setShowNextPw(v => !v)}>
                          {showNextPw ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                        </button>
                      </div>
                      <Input type="password" placeholder="Confirm new password" value={pwForm.confirm} onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))} className="text-sm" data-testid="confirm-password-input" onKeyDown={e => e.key === 'Enter' && handleChangePassword()} />
                      <Button size="sm" className="text-xs" onClick={handleChangePassword} disabled={pwLoading} data-testid="save-password-btn">
                        {pwLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1.5" /> : null}
                        {pwLoading ? 'Saving...' : 'Save Password'}
                      </Button>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary/8 flex items-center justify-center shrink-0">
                      <Clock className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">Account Created</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
                      <ShieldCheck className="h-4 w-4 text-emerald-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">Two-Factor Authentication</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        {twoFALoading ? 'Checking status...' : twoFAEnabled ? 'Enabled — your account is protected' : 'Add an extra layer of security'}
                      </p>
                    </div>
                  </div>
                  {twoFALoading ? (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  ) : twoFAEnabled ? (
                    <Button variant="outline" size="sm" className="text-xs border-red-500/20 text-red-500 hover:bg-red-500/10" onClick={() => { setVerifyCode(''); setShowDisableDialog(true); }} data-testid="2fa-disable-btn">
                      Disable
                    </Button>
                  ) : (
                    <Button size="sm" className="text-xs" onClick={handle2FASetup} disabled={twoFABtnLoading} data-testid="2fa-setup-btn">
                      {twoFABtnLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                      Enable 2FA
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Currency Tab */}
        {activeTab === 'currency' && (
          <Card className="border-border bg-card" data-testid="currency-tab-content">
            <CardHeader className="pb-4">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Globe className="h-4 w-4 text-primary" /> Invoice Currency
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <p className="text-sm text-muted-foreground leading-relaxed">
                Choose the fiat currency you use to price your products. When creating invoices, you'll enter amounts in this currency and the system will automatically convert to the selected crypto token at the current exchange rate.
              </p>

              {currencyLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="currency-grid">
                    {[
                      { code: 'USD', name: 'US Dollar', symbol: '$' },
                      { code: 'EUR', name: 'Euro', symbol: '€' },
                      { code: 'GBP', name: 'British Pound', symbol: '£' },
                      { code: 'CAD', name: 'Canadian Dollar', symbol: 'CA$' },
                      { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
                      { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
                      { code: 'CHF', name: 'Swiss Franc', symbol: 'Fr' },
                      { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$' },
                      { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ' },
                      { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
                      { code: 'BRL', name: 'Brazilian Real', symbol: 'R$' },
                      { code: 'MXN', name: 'Mexican Peso', symbol: 'MX$' },
                      { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$' },
                      { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr' },
                      { code: 'SEK', name: 'Swedish Krona', symbol: 'kr' },
                      { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$' },
                      { code: 'ZAR', name: 'South African Rand', symbol: 'R' },
                      { code: 'PKR', name: 'Pakistani Rupee', symbol: '₨' },
                      { code: 'THB', name: 'Thai Baht', symbol: '฿' },
                      { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM' },
                    ].map(c => (
                      <button
                        key={c.code}
                        onClick={() => setPreferredCurrency(c.code)}
                        className={`flex flex-col items-start p-3 rounded-xl border text-left transition-all ${
                          preferredCurrency === c.code
                            ? 'bg-primary/10 border-primary/40 text-foreground'
                            : 'bg-muted/30 border-border text-muted-foreground hover:border-border/80 hover:bg-muted/50'
                        }`}
                        data-testid={`currency-option-${c.code}`}
                      >
                        <span className={`text-sm font-bold ${preferredCurrency === c.code ? 'text-primary' : ''}`}>{c.symbol}</span>
                        <span className="text-xs font-medium mt-0.5">{c.code}</span>
                        <span className="text-[10px] text-muted-foreground leading-tight mt-0.5 hidden sm:block">{c.name}</span>
                      </button>
                    ))}
                  </div>

                  <div className="p-3.5 rounded-xl bg-muted/30 border border-border flex items-center gap-3">
                    <DollarSign className="h-4 w-4 text-primary shrink-0" />
                    <p className="text-sm text-muted-foreground">
                      Selected: <strong className="text-foreground">{preferredCurrency}</strong> — invoices will show amounts in {preferredCurrency} with live crypto equivalent.
                    </p>
                  </div>

                  <Button
                    onClick={saveCurrency}
                    disabled={currencySaving}
                    className="bg-primary text-white"
                    data-testid="save-currency-btn"
                  >
                    {currencySaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                    Save Currency
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Payment Tolerance Tab */}
        {activeTab === 'tolerance' && (
          <Card className="border-border bg-card" data-testid="tolerance-tab-content">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-2">
                <CardTitle className="text-base font-semibold">Payment Tolerance (%)</CardTitle>
                <Tooltip />
              </div>
            </CardHeader>
            <CardContent className="space-y-5">
              <p className="text-sm text-muted-foreground leading-relaxed">
                Some wallets and exchanges deduct network fees before sending funds.
                Set a tolerance to automatically accept slightly lower amounts.
              </p>

              <div className="flex items-center justify-between p-4 rounded-xl border border-border bg-muted/30" data-testid="tolerance-toggle-row">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-primary/8 flex items-center justify-center shrink-0">
                    <Check className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">Accept payments within tolerance</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {tolerance.enabled ? `Currently accepting payments within ${tolerance.percentage}% of invoice amount` : 'Disabled — exact amount required'}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={tolerance.enabled}
                  onCheckedChange={(v) => updateTolerance('enabled', v)}
                  data-testid="tolerance-toggle"
                />
              </div>

              {tolerance.enabled && (
                <div className="space-y-3" data-testid="tolerance-input-section">
                  <div className="flex items-center gap-3">
                    <div className="relative w-32">
                      <Input
                        type="number" step="0.1" min="0.1" max="10"
                        value={tolerance.percentage}
                        onChange={(e) => updateTolerance('percentage', parseFloat(e.target.value) || 0)}
                        className="pr-8"
                        data-testid="tolerance-percentage-input"
                      />
                      <Percent className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                    <span className="text-sm text-muted-foreground">
                      e.g. For a $100 invoice, payments &ge; ${(100 * (1 - tolerance.percentage / 100)).toFixed(2)} will be accepted
                    </span>
                  </div>
                  {tolerance.percentage > 2 && (
                    <div className="flex items-start gap-2.5 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20" data-testid="tolerance-warning">
                      <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                      <p className="text-sm text-amber-600 dark:text-amber-400">
                        Higher tolerance may result in accepting underpayments.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {toleranceDirty && (
                <Button onClick={saveTolerance} disabled={toleranceSaving} className="bg-primary text-white" data-testid="tolerance-save-btn">
                  {toleranceSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Save Tolerance Settings
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Payment Verification Tab */}
        {activeTab === 'verification' && (
          <Card className="border-border bg-card" data-testid="verification-tab-content">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-2">
                <CardTitle className="text-base font-semibold">Payment Verification</CardTitle>
                <ShieldCheck className="h-4 w-4 text-primary" />
              </div>
            </CardHeader>
            <CardContent className="space-y-5">
              <p className="text-sm text-muted-foreground leading-relaxed">
                Choose how invoice amounts are adjusted for unique payment matching on the blockchain.
                This determines how the system identifies which payment belongs to which invoice.
              </p>

              <div className="space-y-3 mt-2" data-testid="verification-method-section">
                <Select
                  value={verification.method}
                  onValueChange={(v) => { setVerification({ method: v }); setVerificationDirty(true); }}
                >
                  <SelectTrigger className="w-full" data-testid="verification-method-select">
                    <SelectValue placeholder="Select method" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(METHODS).map(([key, m]) => (
                      <SelectItem key={key} value={key} data-testid={`method-option-${key}`}>
                        <span className="flex items-center gap-2">
                          <m.icon className={`h-3.5 w-3.5 ${m.color}`} />
                          {m.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {(() => {
                  const m = METHODS[verification.method];
                  if (!m) return null;
                  const Icon = m.icon;
                  return (
                    <div className={`p-4 rounded-xl border border-border ${m.bg}`} data-testid="method-detail-card">
                      <div className="flex items-start gap-3">
                        <div className={`w-9 h-9 rounded-lg ${m.bg} flex items-center justify-center shrink-0`}>
                          <Icon className={`h-4 w-4 ${m.color}`} />
                        </div>
                        <div className="space-y-2 flex-1">
                          <p className="text-sm font-medium text-foreground">{m.desc}</p>
                          <div className="p-2.5 rounded-lg bg-background/50 border border-border">
                            <p className="text-xs font-mono text-foreground">{m.example}</p>
                          </div>
                          <div className="flex gap-4 text-xs">
                            <div className="flex-1">
                              <p className="text-emerald-500 font-medium mb-0.5">Pros</p>
                              <p className="text-muted-foreground">{m.pros}</p>
                            </div>
                            <div className="flex-1">
                              <p className="text-amber-500 font-medium mb-0.5">Cons</p>
                              <p className="text-muted-foreground">{m.cons}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {verificationDirty && (
                <Button onClick={saveVerification} disabled={verificationSaving} className="bg-primary text-white" data-testid="verification-save-btn">
                  {verificationSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Save Verification Method
                </Button>
              )}
            </CardContent>
          </Card>
        )}

      </div>
    </MerchantLayout>
  );
};

export default MerchantSettings;
